import { FC, useEffect, useState } from 'react';
import { Button, IconButton, Box, Typography } from '@material-ui/core';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import { observer } from 'mobx-react-lite';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { UserEditRequestForm } from 'components/Forms/UserEditRequestForm';
import { UserRejectRequestForm } from 'components/Forms/UserEditRequestForm/UserRejectRequestForm';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { UserNewRequestDataForm } from 'components/Forms/UserEditRequestForm/UserNewRequestForm';

const UsersUpdatedInformation: FC = observer(() => {
  try {
    const classes = useStyles();
    useEffect(() => {
      document.title = 'EasyIdentity: New User Request';
    }, []);
    const params = useParams<{ id: string }>();
    const history = useHistory();
    const [openUserRequestModal, setOpenUserRequestModal] = useState<boolean>(false);
    const [openUserRejectRequestModal, setOpenUserRejectRequestModal] = useState<boolean>(false);
    const [getId, setId] = useState<number | null>(null);
    const [label, setLabel] = useState<string>('');
    const [type, setType] = useState<string>('');
    const [defaultValue, setDefaultValue] = useState<string | null>(null);
    const [disableButton, setisableButton] = useState<boolean>(false);
    const [payloadReject, setPayloadReject] = useState<{ customAttribute: any[] }>({ customAttribute: [] });
    const [payloadEdit, setPayloadEdit] = useState<{ customAttribute: any[] }>({ customAttribute: [] });
    const [UserNewRequestDataModal, setUserNewRequestDataModal] = useState<boolean>(false);
    const {
      userEditRequestTableStore: {
        getnewUserRequestRegistration,
        userChangeRequestView,
        getUserChange,
        acceptChange,
        acceptAllChange,
        rejectAllChange,
        setSelectedId,
        approveNewUserRequest,
      },
      ldapStore: { getUsers },
      userStore: { getEndUserNotification },
      notificationStore: { openNotification },
    } = useStore();

    // Safety check for store initialization
    if (!userChangeRequestView) {
      return <div>Loading...</div>;
    }
    const location = useLocation<any>();
    const locationState = location?.state || null;
    // Fallback: if no state is provided, assume it's a profile change request (not a new request)
    const isNewRequest = Boolean(locationState?.state?.newRequest);
    const [rejectNewUser, setRejectNewUser] = useState<boolean>(false);
    const getUserChangeReq = useQuery(
      'getUserChangeReq',
      () => {
        if (isNewRequest) {
          return getnewUserRequestRegistration(Number(params.id));
        } else {
          return getUserChange(Number(params.id));
        }
      },
      {
        onError: (e) => {
          console.error('Query error:', e);
          openNotification('error', `Can't get users request`);
        },
      }
    );
    const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
      onError: () => openNotification('error', `Can't get users`),
    });
    // const acceptChangeReq = useMutation('acceptChangeReq', acceptChange, {
    //   onSuccess: () => {
    //     openNotification('success', 'Info Updated');
    //     getUserChangeReq.refetch();
    //     getUsersReq.refetch();
    //   },
    //   onError: (e: any) => {
    //     openNotification('error', e?.message);
    //   },
    // });
    const acceptChangeReq = useMutation(({ id, data }: { id: number; data: any }) => acceptChange(id, data), {
      onSuccess: () => {
        openNotification('success', 'Info Updated');
        getUserChangeReq.refetch();
        getUsersReq.refetch();
      },
      onError: (e: any) => {
        openNotification('error', e?.message);
      },
    });
    const getEndUserNotificationReq = useQuery('getEndUserNotificationReq', () => getEndUserNotification(), {});
    const acceptAllChangeReq = useMutation('acceptAllChangeReq', () => acceptAllChange(params.id), {
      onSuccess: () => {
        openNotification('success', 'All changes are accepted');
        getUserChangeReq.refetch();
        getEndUserNotificationReq.refetch();
        getUsersReq.refetch();
      },

      onError: (e) => {
        openNotification('error', `Can't accept all changes`);
      },
    });

    const rejectAllChangeReq = useMutation('rejectAllChangeReq', () => rejectAllChange(params.id, { reason: '' }), {
      onSuccess: () => {
        openNotification('success', 'All changes are rejected successfully');
        getUserChangeReq.refetch();
        getEndUserNotificationReq.refetch();
      },
      onError: (e) => {
        openNotification('error', `Can't reject all changes`);
      },
    });
    const approveUserReq = useMutation('approveAllChangeReq', () => approveNewUserRequest(+params.id), {
      onSuccess: () => {
        openNotification('success', 'All changes are Approved successfully');
        getUsersReq.refetch();
        history.push(handleCompanyUrl('/admin/users-edit-request'));
      },
      onError: (e: any) => {
        openNotification('error', e?.message || e?.toString() || 'Cannot accept request');
      },
    });

    useAppPreloader(
      getUserChangeReq.isFetching ||
        acceptChangeReq.isLoading ||
        approveUserReq.isLoading ||
        acceptAllChangeReq.isLoading ||
        rejectAllChangeReq.isLoading
    );
    const onAccept = (id: number, field: any) => {
      if (field !== null) {
        const payload = {
          customAttribute: [
            {
              ...field,
              status: 'approved',
            },
          ],
        };
        acceptChangeReq.mutate({ id, data: payload });
      } else {
        acceptChangeReq.mutate({ id, data: {} });
        console.warn('Field is null, skipping API call.');
      }
    };

    const AcceptRequest = () => {
      if (isNewRequest) {
        approveUserReq.mutate();
      } else {
        onAcceptAll();
      }
    };
    const onAcceptAll = () => {
      acceptAllChangeReq.mutate();
    };

    const onRejectAll = () => {
      setId(+params.id);
      setSelectedId(+params.id);
      setOpenUserRejectRequestModal(true);
      setType('all');
    };
    const RejectHandler = () => {
      if (isNewRequest) {
        setRejectNewUser(true);
        setUserNewRequestDataModal(true);
      } else {
        onRejectAll();
      }
    };
    const handleClose = () => {
      setSelectedId(0);
      setOpenUserRequestModal(false);
      setOpenUserRejectRequestModal(false);
      setType('');
    };
    const handleEditNewUserRequestData = () => {
      setUserNewRequestDataModal(true);
    };
    const handleEditNewUserRequestDataCloseModal = () => {
      setUserNewRequestDataModal(false);
    };
    const handleClick = (id: number, field: any): void => {
      if (field !== null) {
        const payload = {
          customAttribute: [
            {
              ...field,
            },
          ],
        };
        setPayloadEdit(payload);
      } else {
        setPayloadEdit(field);
      }

      setSelectedId(id);
      setOpenUserRequestModal(true);
    };

    const handleClickReject = (id: number, field: any): void => {
      if (field !== null) {
        const payload = {
          customAttribute: [
            {
              ...field,
              status: 'rejected',
            },
          ],
        };
        setPayloadReject(payload);
      }
      setSelectedId(id);
      setOpenUserRejectRequestModal(true);
    };
    useEffect(() => {
      if (getUserChangeReq.data) {
        const items = [...getUserChangeReq.data];
        // For new registration requests, status is null, so we should enable buttons
        if (isNewRequest) {
          setisableButton(false); // Enable buttons for new requests
        } else {
          // For profile change requests, check if any item has pending status
          const hasPending = items.some((data) => data.status === 'pending');
          if (hasPending) {
            setisableButton(false);
          } else {
            setisableButton(true);
          }
        }
      }
    }, [getUserChangeReq, isNewRequest]);
    return !getUserChangeReq.isFetching ? (
      <div className={classes.container}>
        <div className={classes.topBar}>
          <div className={classes.left}>
            <IconButton
              onClick={() => {
                if (isNewRequest) {
                  history.push(handleCompanyUrl('/admin/users-edit-request'));
                } else {
                  history.push(handleCompanyUrl('/admin/users-edit-request?tab=two'));
                }
              }}
              className={classes.toolbarIcon}
            >
              <ChevronLeftIcon />
            </IconButton>
          </div>
          <div className={classes.right}>
            {isNewRequest && (
              <Button
                style={{ marginRight: '24px' }}
                className={classes.btn}
                color='secondary'
                variant='contained'
                disabled={disableButton}
                onClick={() => handleEditNewUserRequestData()}
              >
                Edit
              </Button>
            )}
            <Button
              style={{ marginRight: '24px' }}
              className={classes.btn}
              color='secondary'
              variant='contained'
              disabled={disableButton}
              onClick={AcceptRequest}
            >
              Accept
            </Button>
            <Button
              className={classes.btn}
              color='primary'
              variant='contained'
              onClick={RejectHandler}
              disabled={disableButton}
            >
              Reject
            </Button>
          </div>
        </div>
        <div className={classes.table}>
          <div className={isNewRequest ? classes.rowNewUser : classes.row}>
            <div className={classes.rowHeading}>USER ATTRIBUTE</div>
            {/* <div className={classes.rowHeading}>OLD VALUE</div> */}
            <div className={classes.rowHeading}> VALUE</div>
            {!isNewRequest && <div className={classes.rowHeading}>Actions</div>}
          </div>
          {!getUserChangeReq.isFetching && userChangeRequestView && userChangeRequestView.length > 0
            ? userChangeRequestView.map((item) => {
                if (item.field.toLowerCase() === 'role') {
                  return null;
                }
                if (item.field === 'customAttribute' && item.customAttributes) {
                  return item.customAttributes.map((attr, index) => (
                    <div key={`${item.id}-${index}`} className={isNewRequest ? classes.rowNewUser : classes.row}>
                      <div className={classes.rowTitle}>{attr.Field.label}</div>
                      {/* <div className={classes.rowElement}>
                    {Array.isArray(attr.Field.oldValue) ? attr.Field.oldValue.join(', ') : attr.Field.oldValue}
                  </div> */}
                      <div
                        style={{
                          color: attr.Field.oldValue !== attr.Field.newValue ? 'green' : '',
                        }}
                        className={classes.rowElement}
                      >
                        {Array.isArray(attr.Field.newValue) ? attr.Field.newValue.join(', ') : attr.Field.newValue}
                      </div>
                      {!isNewRequest && (
                        <>
                          <div className={classes.rowElementIcons}>
                            <IconButton
                              disabled={attr.Field.status === 'pending' ? false : true}
                              className={classes.toolbarIcon}
                            >
                              <EditIcon
                                className={attr.Field.status === 'pending' ? classes.editIcon : ''}
                                onClick={() => {
                                  setId(item.id);
                                  setLabel(attr.Field.label);
                                  setDefaultValue(attr.Field.newValue);
                                  handleClick(item.id, attr.Field);
                                }}
                              />
                            </IconButton>
                            <IconButton
                              disabled={attr.Field.status === 'pending' ? false : true}
                              className={classes.toolbarIcon}
                              onClick={() => {
                                onAccept(item.id, attr.Field);
                              }}
                            >
                              <CheckIcon className={attr.Field.status === 'pending' ? classes.acceptIcon : ''} />
                            </IconButton>
                            <IconButton
                              disabled={attr.Field.status === 'pending' ? false : true}
                              className={classes.toolbarIcon}
                              onClick={() => {
                                setId(item.id);
                                handleClickReject(item.id, attr.Field);
                              }}
                            >
                              <ClearIcon className={attr.Field.status === 'pending' ? classes.rejectIcon : ''} />
                            </IconButton>
                          </div>
                        </>
                      )}
                    </div>
                  ));
                }
                return (
                  <div key={item.id} className={isNewRequest ? classes.rowNewUser : classes.row}>
                    <div className={classes.rowTitle}>{item.field.split(/(?=[A-Z])/).join(' ')}</div>
                    {/* <div className={classes.rowElement}>{item.oldValue}</div> */}
                    {isNewRequest ? (
                      <div className={classes.rowElement}> {item.oldValue} </div>
                    ) : (
                      <div
                        style={{
                          color: item.oldValue !== item.newValue ? 'green' : '',
                        }}
                        className={classes.rowElement}
                      >
                        {item.newValue}
                      </div>
                    )}
                    {!isNewRequest && (
                      <div className={classes.rowElementIcons}>
                        <IconButton disabled={item.status === 'pending' ? false : true} className={classes.toolbarIcon}>
                          <EditIcon
                            className={item.status === 'pending' ? classes.editIcon : ''}
                            onClick={() => {
                              setId(item.id);
                              setLabel(item.field);
                              setDefaultValue(item.newValue);
                              handleClick(item.id, null);
                            }}
                          />
                        </IconButton>
                        <IconButton
                          disabled={item.status === 'pending' ? false : true}
                          className={classes.toolbarIcon}
                          onClick={() => {
                            onAccept(item.id, null);
                          }}
                        >
                          <CheckIcon className={item.status === 'pending' ? classes.acceptIcon : ''} />
                        </IconButton>
                        <IconButton
                          disabled={item.status === 'pending' ? false : true}
                          className={classes.toolbarIcon}
                          onClick={() => {
                            setId(item.id);
                            handleClickReject(item.id, null);
                          }}
                        >
                          <ClearIcon className={item.status === 'pending' ? classes.rejectIcon : ''} />
                        </IconButton>
                      </div>
                    )}
                  </div>
                );
              })
            : ''}
        </div>
        {locationState?.state?.rejected && (
          <Box
            component='div'
            style={{
              whiteSpace: 'normal',
              marginTop: '20px',
              padding: '10px',
              backgroundColor: 'White',
              color: 'Black',
              border: '1px solid',
              height: 'auto',
              borderRadius: 2,
              fontSize: '0.875rem',
              fontWeight: 'bold',
            }}
          >
            <Typography style={{ fontWeight: 'bolder' }}>Reason:</Typography>
            <Typography>{locationState?.state?.reason || 'No reason provided'}</Typography>
          </Box>
        )}
        <UserNewRequestDataForm
          data={userChangeRequestView}
          open={UserNewRequestDataModal}
          handleClose={handleEditNewUserRequestDataCloseModal}
          newUserId={+params.id}
          rejectNewUser={rejectNewUser}
          setRejectNewUser={setRejectNewUser}
        />
        {getId && (
          <UserEditRequestForm
            open={openUserRequestModal}
            defaultValue={defaultValue}
            label={label}
            refetch={() => getUserChangeReq.refetch()}
            handleClose={handleClose}
            id={getId}
            setId={setId}
            payload={payloadEdit}
          />
        )}
        {getId && (
          <UserRejectRequestForm
            open={openUserRejectRequestModal}
            refetch={() => getUserChangeReq.refetch()}
            handleClose={handleClose}
            id={getId}
            setId={setId}
            type={type}
            payload={payloadReject}
          />
        )}
      </div>
    ) : null;
  } catch (error) {
    console.error('Error in UsersUpdatedInformation:', error);
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Typography color='error'>Something went wrong. Please try refreshing the page.</Typography>
      </div>
    );
  }
});

export default UsersUpdatedInformation;
